import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Grid,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { Home, SwitchAccount, LocalFireDepartment, Bolt, TrendingUp } from '@mui/icons-material';
import Dashboard from '../components/Dashboard';
import Header from '../components/Header';
import {
  selectSelectedStudent,
  selectTotalStudentCount
} from '../store/selectors/studentSelectors';
import { fetchProgressSummary, ProgressSummary } from '../services/progressService';

/**
 * The student's profile, replacing the "Profile Page" placeholder that used to sit here.
 *
 * Read-only, and deliberately so. Every field on it — name, class, school, subjects — is
 * owned by the school that enrolled the student, not by the parent holding the login.
 * Letting a parent edit their child's class or school here would silently desynchronise
 * the roster the school administers and the results reporting that depends on it. When
 * something is wrong the fix is a correction at the school, so the screen says that
 * rather than offering an edit button that would write to the wrong place.
 */

const Field: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value && value.trim() !== '' ? value : '—'}
    </Typography>
  </Grid>
);

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const selectedStudent = useSelector(selectSelectedStudent);
  const totalStudentCount = useSelector(selectTotalStudentCount);

  const [summary, setSummary] = useState<ProgressSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    if (!selectedStudent) {
      navigate('/select-student');
    }
  }, [selectedStudent, navigate]);

  useEffect(() => {
    let cancelled = false;
    const studentId = selectedStudent?.id;
    if (!studentId) return undefined;

    setLoadingSummary(true);
    fetchProgressSummary(studentId).then((s) => {
      if (cancelled) return;
      setSummary(s);
      setLoadingSummary(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedStudent?.id]);

  if (!selectedStudent) return null;

  const body = (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Profile
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar src={selectedStudent.photo} sx={{ width: 72, height: 72, fontSize: 28 }}>
            {selectedStudent.name?.charAt(0) ?? 'S'}
          </Avatar>
          <Box>
            <Typography variant="h5">{selectedStudent.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedStudent.school}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Field label="Class" value={selectedStudent.class} />
          <Field label="Grade" value={selectedStudent.grade} />
          <Field label="School" value={selectedStudent.school} />
          <Field label="Registered mobile" value={selectedStudent.phoneNumber} />
        </Grid>

        {selectedStudent.subscribedSubjects &&
          selectedStudent.subscribedSubjects.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2, mb: 0.5 }}
              >
                Subjects
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedStudent.subscribedSubjects.map((s) => (
                  <Chip key={s} size="small" label={s} />
                ))}
              </Box>
            </>
          )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          These details come from your school's records. If something is wrong, ask your
          school to correct it — changing it here would not update their roster.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          At a glance
        </Typography>

        {loadingSummary ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : summary ? (
          <List dense disablePadding>
            <ListItem disableGutters>
              <LocalFireDepartment sx={{ mr: 1.5, color: '#e2622a' }} />
              <ListItemText
                primary={`${summary.currentStreakDays}-day streak`}
                secondary={`Longest: ${summary.longestStreakDays} days · ${summary.activeDaysTotal} active days`}
              />
            </ListItem>
            <ListItem disableGutters>
              <Bolt sx={{ mr: 1.5, color: 'primary.main' }} />
              <ListItemText
                primary={`${summary.xp} XP`}
                secondary={`${summary.badges?.filter((b) => b.earned).length ?? 0} badges earned`}
              />
            </ListItem>
            <ListItem disableGutters>
              <TrendingUp sx={{ mr: 1.5, color: 'primary.main' }} />
              <ListItemText
                primary={
                  summary.accuracyPct != null
                    ? `${summary.accuracyPct.toFixed(0)}% accuracy`
                    : 'No questions answered yet'
                }
                secondary={`${summary.questionsCorrect}/${summary.questionsAttempted} correct · ${summary.sessionsCompleted} papers completed`}
              />
            </ListItem>
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Your stats will appear here once you have answered some questions.
          </Typography>
        )}

        <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/progress')}>
          See full progress
        </Button>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<Home />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
        {totalStudentCount > 1 && (
          <Button
            variant="outlined"
            startIcon={<SwitchAccount />}
            onClick={() => navigate('/select-student')}
          >
            Switch student
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Dashboard
      constrained
      header={<Header selectedStudent={selectedStudent} />}
      students={body}
    />
  );
};

export default ProfilePage;
